import { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";

/**
 * Returns a debounced version of a value using lodash's debounce.
 * The value will update only after `delay` ms have elapsed without
 * any further changes.
 */
export function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  // Memoise the debounced setter so it's stable across renders
  const debouncedSetter = useMemo(
    () => debounce((v: T) => setDebouncedValue(v), delay),
    [delay],
  );

  useEffect(() => {
    debouncedSetter(value);

    return () => {
      debouncedSetter.cancel();
    };
  }, [value, debouncedSetter]);

  return debouncedValue;
}
